import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  CheckCircle2,
  XCircle,
  ArrowRight,
  ChevronDown,
  Sparkles,
  Users,
  Briefcase,
  Building,
  ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    } 
  }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function PricingPlans({ viewport, isRTL }) {
  const { t } = useTranslation();
  
  const { isMobile = false } = viewport || {};
  
  const [isAnnual, setIsAnnual] = useState(true);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const toggleBillingCycle = () => {
    setIsAnnual(!isAnnual);
  };

  const plansData = [
    {
      id: 'developer',
      icon: Users,
      priceMonthly: '---',
      priceAnnually: '---',
      color: 'hsl(var(--muted-foreground))',
      borderColor: 'hsl(var(--border))',
      featured: false,
      features: [
        { textKey: 'devices', valueKey: 'developer.devices' },
        { textKey: 'history', valueKey: 'developer.history' },
        { textKey: 'users', valueKey: 'developer.users' },
        { textKey: 'support', valueKey: 'developer.support' },
        { textKey: 'advancedFeatures', included: false },
        { textKey: 'apiAccess', included: false },
        { textKey: 'whiteLabel', included: false },
        { textKey: 'customIntegrations', included: false },
      ]
    },
    {
      id: 'business',
      icon: Briefcase,
      priceMonthly: '---',
      priceAnnually: '---',
      color: 'hsl(var(--primary))',
      borderColor: 'hsl(var(--primary))',
      featured: true,
      features: [
        { textKey: 'devices', valueKey: 'business.devices' },
        { textKey: 'history', valueKey: 'business.history' },
        { textKey: 'users', valueKey: 'business.users' },
        { textKey: 'support', valueKey: 'business.support' },
        { textKey: 'advancedFeatures', included: true },
        { textKey: 'apiAccess', included: true },
        { textKey: 'whiteLabel', included: false },
        { textKey: 'customIntegrations', included: false },
      ]
    },
    {
      id: 'enterprise',
      icon: Building,
      priceMonthly: null,
      priceAnnually: null,
      color: 'hsl(var(--secondary))',
      borderColor: 'hsl(var(--secondary))',
      featured: false,
      features: [
        { textKey: 'devices', valueKey: 'enterprise.devices' },
        { textKey: 'history', valueKey: 'enterprise.history' },
        { textKey: 'users', valueKey: 'enterprise.users' },
        { textKey: 'support', valueKey: 'enterprise.support' },
        { textKey: 'advancedFeatures', included: true },
        { textKey: 'apiAccess', included: true },
        { textKey: 'whiteLabel', included: true },
        { textKey: 'customIntegrations', included: true },
      ],
      //ctaKey: 'contactSales'
    }
  ];

  const plans = plansData.map(plan => ({
    ...plan,
    name: t(`homepage.pricing.plans.${plan.id}.name`),
    description: t(`homepage.pricing.plans.${plan.id}.description`),
    price: isAnnual ? plan.priceAnnually : plan.priceMonthly,
    billingCycleText: isAnnual ? t('homepage.pricing.yearShort', '/yr') : t('homepage.pricing.monthShort', '/mo'),
    features: plan.features.map(f => ({
      text: t(`homepage.pricing.featuresList.${f.textKey}`),
      value: f.valueKey ? t(`homepage.pricing.plans.${f.valueKey}`) : null, 
      included: f.included,
      textKey: f.textKey
    })),
    ctaText: plan.ctaKey ? t(`homepage.pricing.plans.${plan.ctaKey}`) : t('homepage.pricing.plans.getStarted')
  }));

  const commonFeaturesList = [
    { id: 'devices', name: t('homepage.pricing.featuresList.devices')},
    { id: 'history', name: t('homepage.pricing.featuresList.history')},
    { id: 'users', name: t('homepage.pricing.featuresList.users')},
    { id: 'support', name: t('homepage.pricing.featuresList.support')},
    { id: 'advancedFeatures', name: t('homepage.pricing.featuresList.advancedFeatures')},
    { id: 'apiAccess', name: t('homepage.pricing.featuresList.apiAccess')},
    { id: 'whiteLabel', name: t('homepage.pricing.featuresList.whiteLabel')},
    { id: 'customIntegrations', name: t('homepage.pricing.featuresList.customIntegrations')}
  ];

  const displayedFeaturesCount = isMobile ? 4 : (showAllFeatures ? commonFeaturesList.length : 6);

  return (
    <section
      id="pricing-plans"
      className="relative py-20 md:py-28 lg:py-32 bg-background overflow-hidden"
    >
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerChildren}
          className="max-w-2xl mx-auto text-center mb-12 md:mb-16"
        >
          <motion.div variants={fadeInUp}>
            <Badge
              variant="outline"
              className="mb-3 px-3 py-1 text-xs font-medium rounded-full border-primary/30 bg-primary/10 text-primary"
            >
              {t('homepage.pricing.badge', 'Flexible & Transparent')}
            </Badge>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-foreground">
              {t('homepage.pricing.title', 'Choose Your Perfect Plan')}
            </h2>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <p className="text-md md:text-lg text-muted-foreground max-w-xl mx-auto">
              {t('homepage.pricing.subtitle', 'Simple, predictable pricing that scales with your needs. No hidden fees, just powerful features.')}
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          className="flex items-center justify-center gap-3 mb-10 md:mb-12"
        >
          <span className={cn("text-sm font-medium transition-colors", !isAnnual ? "text-primary" : "text-muted-foreground")}>
            {t('homepage.pricing.monthly', 'Monthly')}
          </span>
          <Switch
            checked={isAnnual}
            onCheckedChange={toggleBillingCycle}
            aria-label={t('homepage.pricing.toggleBilling', 'Toggle billing cycle')}
            id="billing-cycle-switch"
            className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
          />
          <span className={cn("text-sm font-medium transition-colors", isAnnual ? "text-primary" : "text-muted-foreground")}>
            {t('homepage.pricing.annual', 'Annually')}
          </span>
          {isAnnual && (
            <Badge 
                variant="outline"
                className="border-green-500/50 bg-green-500/10 text-green-600 text-xs px-2 py-0.5"
            >
                {t('homepage.pricing.save20', 'Save 20%')}
            </Badge>
          )}
        </motion.div>

        <motion.div
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: isMobile ? 0.05 : 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch"
        >
          {plans.map((plan, index) => {
            const PlanIcon = plan.icon;
            const isFeaturedDesktop = plan.featured && !isMobile;
            const orderClass = isMobile && plan.featured ? "order-first" : (isMobile && !plan.featured && plans.some(p => p.featured) ? `order-${index + 1}` : "");

            return (
              <motion.div 
                key={plan.id} 
                variants={fadeInUp} 
                className={cn("flex", orderClass)}
              >
                <Card className={cn(
                  "flex flex-col w-full rounded-xl overflow-hidden border bg-card",
                  "transition-all duration-300 ease-in-out group",
                  plan.featured 
                    ? "shadow-xl shadow-primary/10 hover:shadow-primary/20 scale-[1.02] hover:scale-[1.03]" 
                    : "shadow-md hover:shadow-lg hover:border-muted-foreground/30",
                   isFeaturedDesktop ? "relative" : ""
                )}
                style={{borderColor: plan.featured ? plan.color : plan.borderColor}}
                >
                  {plan.featured && (
                    <div className={cn(
                        "absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20",
                        !isMobile && "-top-3.5" 
                    )}>
                        <Badge 
                            variant="default" 
                            className="px-3 py-1 text-xs font-semibold rounded-full shadow-md"
                            style={{backgroundColor: plan.color, color: `hsl(var(--primary-foreground))`}}
                        >
                            <Sparkles size={14} className="mr-1.5" />
                            {t('homepage.pricing.mostPopular', 'Most Popular')}
                        </Badge>
                    </div>
                  )}
                  <CardHeader className={cn("p-6 text-center items-center", plan.featured && !isMobile && "pt-10")}>
                     <div className="p-3 rounded-lg mb-3 inline-flex items-center justify-center"
                          style={{ backgroundColor: `color-mix(in srgb, ${plan.color} 15%, transparent)`}}>
                        <PlanIcon size={28} style={{color: plan.color}} />
                     </div>
                    <CardTitle className="text-xl font-bold text-foreground">{plan.name}</CardTitle>
                    <CardDescription className="text-sm text-muted-foreground min-h-[40px] line-clamp-2">{plan.description}</CardDescription>
                  </CardHeader>
                  
                  <CardContent className="p-6 flex flex-col flex-grow">
                    <div className="text-center mb-6">
                      {plan.price !== null ? (
                        <div className="flex items-baseline justify-center">
                          <span className="text-4xl font-extrabold text-foreground">{plan.price}</span>
                          <span className="text-sm font-medium text-muted-foreground ml-1">{plan.billingCycleText}</span>
                        </div>
                      ) : (
                        <span className="text-2xl font-bold text-foreground">{t('homepage.pricing.custom', 'Custom')}</span>
                      )}
                    </div>
                    
                    <ul className="space-y-2.5 text-sm mb-8 flex-grow">
                      {plan.features.slice(0, isMobile ? 4 : plan.features.length).map((feature, i) => (
                        <li key={i} className="flex items-start">
                          {feature.included ? (
                            <CheckCircle2 size={16} className="mr-2.5 mt-0.5 flex-shrink-0" style={{color: plan.color}} />
                          ) : (
                            <XCircle size={16} className="mr-2.5 mt-0.5 flex-shrink-0 text-muted-foreground/70" />
                          )}
                          <span className={cn(
                            "text-muted-foreground", 
                            !feature.included && "opacity-70",
                            feature.value && "font-medium text-foreground"
                          )}>
                            {feature.text}{feature.value ? `: ${feature.value}` : ""}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <Button 
                      variant={plan.featured ? 'default' : 'outline'} 
                      size="lg"
                      className="w-full group mt-auto transition-all duration-200 ease-in-out" 
                      style={
                        plan.featured 
                          ? {backgroundColor: plan.color, color: `hsl(var(--primary-foreground))`, borderColor: plan.color} 
                          : {borderColor: plan.color, color: plan.color}
                      }
                      onMouseEnter={(e) => { if (!plan.featured) { e.currentTarget.style.backgroundColor = `color-mix(in srgb, ${plan.color} 15%, transparent)`; }}}
                      onMouseLeave={(e) => { if (!plan.featured) { e.currentTarget.style.backgroundColor = 'transparent'; }}}
                    >
                      {plan.ctaText}
                      {!plan.ctaKey?.includes('Contact') && (
                        isRTL 
                          ? <ChevronLeft size={18} className="ml-2 group-hover:-translate-x-1 transition-transform" /> 
                          : <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {!isMobile && (
          <motion.div 
            variants={fadeInUp} 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-16 md:mt-20"
          >
            <div className="text-center mb-8 md:mb-10">
                <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                    {t('homepage.pricing.compareFeatures', 'Compare All Features')}
                </h3>
                <p className="text-md text-muted-foreground">
                    {t('homepage.pricing.compareSubtitle', 'Detailed breakdown of what each plan offers.')}
                </p>
            </div>
            <Card className="overflow-hidden border-border/20 bg-card shadow-lg rounded-xl">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px] text-sm">
                        <thead className="bg-muted/30 border-b border-border/30">
                            <tr>
                                <th className="p-4 text-left font-semibold text-foreground w-2/5 sticky left-0 bg-muted/30 z-10">{t('common.feature','Feature')}</th>
                                {plans.map(plan => (
                                    <th key={plan.id} className="p-4 text-center font-semibold" style={{color: plan.color}}>{plan.name}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {commonFeaturesList.slice(0, displayedFeaturesCount).map(featureDef => (
                                <tr key={featureDef.id} className="border-b border-border/20 last:border-b-0 hover:bg-muted/20 transition-colors duration-150">
                                    <td className="p-4 font-medium text-foreground sticky left-0 bg-inherit z-[5] group-hover:bg-muted/20">{featureDef.name}</td>
                                    {plans.map(plan => {
                                        const planSpecificFeature = plansData.find(p => p.id === plan.id)?.features.find(f => f.textKey === featureDef.id);
                                        let content;

                                        if (planSpecificFeature) {
                                            const translatedFeature = plan.features.find(pf => pf.textKey === featureDef.id);
                                            if (planSpecificFeature.valueKey && translatedFeature?.value) {
                                                content = <span className="text-muted-foreground">{translatedFeature.value}</span>;
                                            } else if (planSpecificFeature.included) {
                                                content = <CheckCircle2 size={18} className="mx-auto" style={{color: plan.color}} />;
                                            } else {
                                                content = <XCircle size={18} className="mx-auto text-muted-foreground/60" />;
                                            }
                                        } else {
                                          content = <XCircle size={18} className="mx-auto text-muted-foreground/60" />;
                                        }
                                        return <td key={`${plan.id}-${featureDef.id}`} className="p-4 text-center">{content}</td>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {commonFeaturesList.length > 6 && (
                    <div className="p-4 text-center border-t border-border/20 bg-muted/30">
                        <Button 
                            variant="ghost" 
                            onClick={() => setShowAllFeatures(!showAllFeatures)} 
                            className="text-sm text-primary hover:text-primary/80 hover:bg-primary/10"
                        >
                            {showAllFeatures ? t('common.showLess', 'Show Less') : t('common.showMore', 'Show More Features')}
                            <ChevronDown size={16} className={cn("ml-1.5 transition-transform", showAllFeatures && "rotate-180")} />
                        </Button>
                    </div>
                )}
            </Card>
          </motion.div>
        )}
      </div>
    </section>
  );
} 
